//* Import react hooks
import { useState, useCallback } from "react";

//* Import react-query hooks
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

//! Import API functions
import { demandsAPI } from "../demand/demandAPI";

//? Import types from pagination
import { Pagination } from "../../services/stores/types";

//? Import types
import type {
  DemandsDetails,
  CreateDemandsInput,
  UpdateDemandsInput,
} from "./types/types";

/**
 * Custom hook to manage demands.
 *
 * @param {string} token - The authentication token.
 * @returns {object} - An object containing demands data, loading state, error state, search state, pagination state, and various mutation functions.
 *
 * @property {Array} demands - The list of demands.
 * @property {boolean} loading - Indicates if the data is currently loading.
 * @property {string | null} error - The error message if an error occurred.
 * @property {string} search - The current search term.
 * @property {function} setSearch - Function to set the search term.
 * @property {object} pagination - The pagination state.
 * @property {function} setPagination - Function to set the pagination state.
 * @property {function} refetch - Function to refetch the demands data.
 * @property {function} createDemand - Function to create a new demand.
 * @property {function} updateDemand - Function to update an existing demand.
 * @property {function} deleteDemand - Function to delete a demand.
 * @property {function} getDemandById - Function to get a demand by its ID.
 * @property {function} changePage - Function to change the current page.
 * @property {boolean} isCreating - Indicates if a demand is currently being created.
 * @property {boolean} isUpdating - Indicates if a demand is currently being updated.
 * @property {boolean} isDeleting - Indicates if a demand is currently being deleted.
 * @property {string | null} createdDemandId - The ID of the newly created demand.
 */
export const useDemands = (token: string) => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  const [createdDemandId, setCreatedDemandId] = useState<string | null>(null);

  const queryKey = [
    "demands",
    {
      search,
      currentPage: pagination.currentPage,
      itemsPerPage: pagination.itemsPerPage,
    },
  ] as const;

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      demandsAPI.getAll(
        search,
        pagination.currentPage,
        token,
        pagination.itemsPerPage
      ),
    staleTime: 5 * 60 * 1000,
  });

  const demands = data?.demands || [];
  const fetchedPagination = data?.pagination || pagination;

  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const changePage = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  const createDemandMutation = useMutation({
    mutationFn: (demandData: CreateDemandsInput) =>
      demandsAPI.create(demandData, token),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["demands"] });
      setCreatedDemandId(data.id);
    },
  });

  const updateDemandMutation = useMutation({
    mutationFn: ({ id, ...demandData }: UpdateDemandsInput) =>
      demandsAPI.update(id, { ...demandData }, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["demands"] });
    },
  });

  const deleteDemandMutation = useMutation({
    mutationFn: (id: string) => demandsAPI.delete(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["demands"] });
    },
  });

  const getDemandById = useCallback(
    async (id: string): Promise<DemandsDetails | null> => {
      try {
        const DemandDetails = await demandsAPI.getById(id, token);
        return DemandDetails;
      } catch (err) {
        console.error("Error fetching Demand by ID:", err);
        return null;
      }
    },
    [token]
  );

  return {
    demands,
    loading: isLoading || isFetching,
    error: isError ? (error as Error)?.message : null,
    search,
    setSearch: handleSearch,
    pagination: fetchedPagination,
    setPagination,
    refetch,
    createDemand: createDemandMutation.mutateAsync,
    updateDemand: updateDemandMutation.mutateAsync, 
    deleteDemand: deleteDemandMutation.mutateAsync, 
    getDemandById,
    changePage,
    isCreating: createDemandMutation.isPending,
    isUpdating: updateDemandMutation.isPending,
    isDeleting: deleteDemandMutation.isPending,
    createdDemandId,
  };
};
